/* eslint-disable react-hooks/rules-of-hooks */
/**
 * Headlines Module - Discussion Boards for BoltOS
 * 
 * WISHLIST: See docs/FEATURE_REQUESTS.md for future enhancement ideas including:
 * - Collapsible comments with count badges
 * - Emoji/GIF support in comments
 * - Sharing posts with comments included
 * - And many more features!
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { UserProfile, HeadlineItem, Comment } from "@shared/types";
import { ROLES } from "@shared/constants";
import {
  hasExecutiveLocationAccess,
  isTeamLeadRole,
  normalizeRole,
} from "@shared/role-access";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/fetchWithAuth";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { 
  buildLocationQuery, 
  filterByLocation,
  getLocationDisplayName,
  logLocationQuery 
} from "@/lib/locationUtils";
import { logger } from "@/lib/logger";
import { Checkbox } from "@/components/ui/checkbox";
import { DeleteButton } from "@/components/DeleteButton";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { BoardSelector } from "@/components/common/BoardSelector";
import { LocationSelector } from "@/components/common/LocationSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface HeadlinesProps {
  user: UserProfile;
}

// Location-based boards
const LOCATION_BOARDS = [
  { id: '22222222-2222-2222-2222-222222222201', name: 'LVL' },
  { id: '22222222-2222-2222-2222-222222222202', name: 'RVD' },
  { id: '22222222-2222-2222-2222-222222222203', name: 'ECOBB' },
  { id: '22222222-2222-2222-2222-222222222207', name: 'CTN' },
  { id: '22222222-2222-2222-2222-222222222204', name: 'JCITY' },
  { id: '22222222-2222-2222-2222-222222222205', name: 'REG' },
  { id: '22222222-2222-2222-2222-222222222206', name: 'DEC' },
  { id: '22222222-2222-2222-2222-222222222208', name: 'BOLT TaskForce' },
  { id: '22222222-2222-2222-2222-222222222209', name: 'Bolt Global' },
];

// Role-based boards
const ROLE_BOARDS = [
  { id: 'executive', name: 'Executive Headlines', field: 'executiveHeadlines' },
  { id: 'teamLeadGlobal', name: 'Team Lead Global Headlines', field: 'teamLeadGlobalHeadlines' },
  { id: 'teamMemberGlobal', name: 'Team Member Global Headlines', field: 'teamMemberGlobalHeadlines' },
  { id: 'billingAuditing', name: 'Billing & Auditing Headlines', field: 'billingAndAuditingHeadlines' },
  { id: 'training', name: 'Training Headlines', field: 'trainingHeadlines' },
  { id: 'trainer', name: 'Trainer Headlines', field: 'trainerHeadlines' },
  { id: 'executiveTaskforce', name: 'Executive Taskforce', field: 'executiveTaskforceHeadlines' },
];

// Map board IDs to actual role names for backend
const BOARD_ID_TO_ROLE: Record<string, string> = {
  'executive': ROLES.EXECUTIVE_TEAM,
  'teamLeadGlobal': ROLES.TEAM_LEAD_OPTICAL,
  'teamMemberGlobal': ROLES.TEAM_MEMBER,
  'billingAuditing': 'BillingAuditing',
  'training': 'TrainingLevel',
  'trainer': 'Trainer',
  'executiveTaskforce': ROLES.TASK_FORCE
};

// Debug the mappings
console.log('🗺️ Board ID to Role mappings:', BOARD_ID_TO_ROLE);

// Reverse mapping for displaying headlines
const ROLE_TO_BOARD_ID: Record<string, string> = {
  [ROLES.EXECUTIVE_TEAM]: 'executive',
  [ROLES.TEAM_LEAD_DOCTOR]: 'teamLeadGlobal',
  [ROLES.TEAM_LEAD_OPTICAL]: 'teamLeadGlobal',
  [ROLES.TEAM_MEMBER]: 'teamMemberGlobal',
  'BillingAuditing': 'billingAuditing',
  'TrainingLevel': 'training',
  'Trainer': 'trainer',
  [ROLES.TASK_FORCE]: 'executiveTaskforce',
  'TeamLead': 'teamLeadGlobal',
  'TeamLeadClinic': 'teamLeadGlobal',
  'BoltEyeTaskForce': 'executiveTaskforce'
};

// Popular emojis for quick access
const POPULAR_EMOJIS = ['😀', '👍', '❤️', '🎉', '🔥', '💪', '🙌', '👏', '😂', '🤔', '💡', '✨', '🎯', '📈', '🏆', '⭐'];

// GIF search API (using Giphy)
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || 'wkzAfhXRiJkPJcONv24Im7zjGM7Uf7GZ';

export default function Headlines({ user }: HeadlinesProps) {
  // ALL HOOKS MUST BE DECLARED FIRST - BEFORE ANY CONDITIONALS
  const { selectedLocationIds, setSelectedLocationIds, locations: contextLocations, isLoadingLocations, locationsOrganizationId, locationsError } = useLocation();
  const { currentOrganizationId, isSwitching } = useOrganization();
  const locations = useMemo(() => contextLocations.filter(loc =>
    (loc.organizationId || (loc as any).organization_id) === currentOrganizationId),
  [contextLocations, currentOrganizationId]);
  const locationsReady = Boolean(currentOrganizationId) &&
    locationsOrganizationId === currentOrganizationId && !isLoadingLocations && !isSwitching;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedBoard, setSelectedBoard] = useState<string>("");
  const [boardType, setBoardType] = useState<'location' | 'role'>('location');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearchTerm, setGifSearchTerm] = useState("");
  const [gifResults, setGifResults] = useState<any[]>([]);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [trendingGifs, setTrendingGifs] = useState<any[]>([]);  // Store trending GIFs separately
  const [selectedTab, setSelectedTab] = useState<'all' | 'location' | 'role'>('all');
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const gifScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [postFiles, setPostFiles] = useState<File[]>([]);
  const [postMentions, setPostMentions] = useState<string[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentContent, setCommentContent] = useState<Record<string, string>>({});
  const [commentFiles, setCommentFiles] = useState<Record<string, File[]>>({});
  const [commentMentions, setCommentMentions] = useState<Record<string, string[]>>({});
  const [commentMentionOpen, setCommentMentionOpen] = useState<string | null>(null);
  const [commentMentionSearch, setCommentMentionSearch] = useState('');
  const [replyingTo, setReplyingTo] = useState<Record<string, string>>({});
  const [editingPost, setEditingPost] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editingHeadline, setEditingHeadline] = useState<HeadlineItem | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHeadlineContent, setEditHeadlineContent] = useState({ title: '', content: '' });
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharingPost, setSharingPost] = useState<HeadlineItem | null>(null);
  const [shareMessage, setShareMessage] = useState('');
  const [selectedShareBoard, setSelectedShareBoard] = useState('');
  const [shareBoardType, setShareBoardType] = useState<'location' | 'role'>('location');
  const [commentsData, setCommentsData] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [commentEmojiOpenId, setCommentEmojiOpenId] = useState<string | null>(null);
  const [commentGifOpenId, setCommentGifOpenId] = useState<string | null>(null);
  const [headlineLocalFiles, setHeadlineLocalFiles] = useState<Record<string, File[]>>({});
  // Bulk attachments feature (Phase 2): feature-toggle via URL ?bulk=1 (default off for stability)
  const bulkAttachmentsEnabled = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('bulk') === '1';
    } catch { return false; }
  }, []);
  const [visibleHeadlineIds, setVisibleHeadlineIds] = useState<string[]>([]);
  // Keep visible ids in sync with currently rendered headlines (simple, non-virtualized)
  const prevIdsRef = useRef<string[]>([]);
  useEffect(() => {
    // schedule after paint; guard against unnecessary setState to avoid loops
    const id = window.requestAnimationFrame(() => {
      try {
        // @ts-ignore - sortedHeadlines is defined later in this component
        const anyHeadlines: any[] = (typeof sortedHeadlines !== 'undefined') ? sortedHeadlines : [];
        const candidate = anyHeadlines.map(h => h?.id).filter(Boolean).slice(0, 50) as string[];
        const prev = prevIdsRef.current;
        const unchanged = prev.length === candidate.length && prev.every((v, i) => v === candidate[i]);
        if (!unchanged) {
          prevIdsRef.current = candidate;
          setVisibleHeadlineIds(candidate);
        }
      } catch {}
    });
    return () => window.cancelAnimationFrame(id);
  });

  const { data: bulkAttachmentsData } = useQuery({
    queryKey: ['/api/attachments/bulk', visibleHeadlineIds, currentOrganizationId],
    queryFn: async () => {
      const idsCsv = visibleHeadlineIds.join(',');
      if (!idsCsv) return { attachmentsByHeadline: {} } as any;
      let url = `/api/attachments/bulk?headlineIds=${encodeURIComponent(idsCsv)}`;
      if (currentOrganizationId) url += `&organizationId=${encodeURIComponent(currentOrganizationId)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.status === 304) return null;
      if (!res.ok) throw new Error('Bulk attachments fetch failed');
      return res.json();
    },
    staleTime: 15000,
    gcTime: 60000,
    retry: (failureCount, error: any) => {
      const status = (error?.status || 0);
      return status !== 429 && status < 500 && failureCount < 1;
    },
    enabled: bulkAttachmentsEnabled && visibleHeadlineIds.length > 0,
  });
  const bulkMap: Record<string, any[]> = (bulkAttachmentsData as any)?.attachmentsByHeadline || {};
  
  function PersistedAttachments({ headlineId, organizationId }: { headlineId: string; organizationId?: string }) {
    const safeOrg = organizationId ?? undefined;
    // Prefer bulk data if available to avoid per-item fetches
    if (bulkAttachmentsEnabled) {
      const items = bulkMap[headlineId] || [];
      if (!items || items.length === 0) return null;
      return (
        <div className="mt-2">
          <div className="text-xs text-neutral-500 mb-1">{items.length} file{items.length > 1 ? 's' : ''} attached</div>
          <div className="flex flex-wrap gap-2">
            {items.map((att: any) => {
              const isImage = (att.mime_type || '').startsWith('image/');
              const onDownload = () => {
                const base = `/api/attachments/${att.id}/download`;
                const url = safeOrg ? `${base}?organizationId=${encodeURIComponent(safeOrg)}` : base;
                window.open(url, '_blank');
              };
              return (
                <div key={att.id} className="relative group">
                  {isImage ? (
                    <div className="relative">
                      <button onClick={onDownload} className="block">
                        <img 
                          src={`${safeOrg ? `/api/attachments/${att.id}/download?organizationId=${encodeURIComponent(safeOrg)}&inline=1` : `/api/attachments/${att.id}/download?inline=1`}`}
                          alt={att.file_name}
                          className="h-40 w-40 object-cover rounded hover:opacity-90 transition-opacity" 
                        />
                      </button>
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 text-xs"
                        onClick={onDownload}
                      >
                        <span className="mdi mdi-download mr-1" /> Download
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="secondary" size="sm" className="h-8 text-xs" onClick={onDownload}>
                        <span className="mdi mdi-paperclip mr-1" /> {att.file_name}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onDownload}>
                        <span className="mdi mdi-download" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Fallback (disabled by default): per-item fetch
    const orgParam = safeOrg 
      ? `?organizationId=${encodeURIComponent(safeOrg)}&headlineId=${encodeURIComponent(headlineId)}` 
      : `?headlineId=${encodeURIComponent(headlineId)}`;
    const { data, isLoading } = useQuery({
      queryKey: ['attachments', headlineId, organizationId],
      queryFn: () => apiRequest(`/api/attachments${orgParam}`),
      staleTime: 15000,
      gcTime: 60000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      enabled: !bulkAttachmentsEnabled && !!headlineId
    });
    if (isLoading) return null;
    const items = (data as any)?.items || [];
    if (items.length === 0) return null;
    return (
      <div className="mt-2">
        <div className="text-xs text-neutral-500 mb-1">{items.length} file{items.length > 1 ? 's' : ''} attached</div>
        <div className="flex flex-wrap gap-2">
          {items.map((att: any) => {
            const isImage = (att.mime_type || '').startsWith('image/');
            const onDownload = () => {
              const base = `/api/attachments/${att.id}/download`;
              const url = safeOrg ? `${base}?organizationId=${encodeURIComponent(safeOrg)}` : base;
              window.open(url, '_blank');
            };
            return (
              <div key={att.id} className="relative group">
                {isImage ? (
                  <div className="relative">
                    <button onClick={onDownload} className="block">
                      <img 
                        src={`${safeOrg ? `/api/attachments/${att.id}/download?organizationId=${encodeURIComponent(safeOrg)}&inline=1` : `/api/attachments/${att.id}/download?inline=1`}`}
                        alt={att.file_name}
                        className="h-40 w-40 object-cover rounded hover:opacity-90 transition-opacity" 
                      />
                    </button>
                    {/* Explicit Download button overlay */}
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 text-xs"
                      onClick={onDownload}
                    >
                      <span className="mdi mdi-download mr-1" /> Download
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <Button variant="secondary" size="sm" className="h-8 text-xs" onClick={onDownload}>
                      <span className="mdi mdi-paperclip mr-1" /> {att.file_name}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onDownload}>
                      <span className="mdi mdi-download" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  const [commentLocalFiles, setCommentLocalFiles] = useState<Record<string, File[]>>({});
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Fetch org-scoped users for like tooltips
  const { data: usersForOrg = [] } = useQuery({
    queryKey: ['/api/users', currentOrganizationId],
    queryFn: async () => {
      const url = currentOrganizationId ? `/api/users?organizationId=${encodeURIComponent(currentOrganizationId)}` : '/api/users';
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load users');
      return res.json();
    },
    staleTime: 0,
    enabled: !!currentOrganizationId,
  });

  const userIdToName = useMemo<Record<string, string>>(() => {
    const entries = (usersForOrg as any[]).map(u => [u.id, u.fullName || u.full_name || u.email || 'User']);
    return Object.fromEntries(entries);
  }, [usersForOrg]);

  // Initialize selected role boards - must be before conditionals
  const [selectedRoleBoards, setSelectedRoleBoards] = useState<string[]>([]);

  // Effect hooks must also be before conditionals
  useEffect(() => {
    // Initialize role board selections based on user's access
    const accessibleRoleBoards = ROLE_BOARDS.filter(board => {
      // CEOs have access to everything
      if (hasExecutiveLocationAccess(user.role, Boolean(user.isSuperAdmin))) return true;
      
      // Check explicit permissions
      if (user.headlineAccess && user.headlineAccess[board.field as keyof typeof user.headlineAccess]) {
        return true;
      }
      
      // Automatic access based on role
      const normalizedUserRole = normalizeRole(user.role);
      if (board.id === 'teamMemberGlobal' && normalizedUserRole === ROLES.TEAM_MEMBER) return true;
      if (board.id === 'teamLeadGlobal' && isTeamLeadRole(user.role)) return true;
      if (board.id === 'executive' && normalizedUserRole === ROLES.EXECUTIVE_TEAM) return true;
      if (board.id === 'executiveTaskforce' && normalizedUserRole === ROLES.TASK_FORCE) return true;
      
      return false;
    });
    
    // DO NOT select all accessible role boards by default - this filters out location headlines!
    // Start with empty selection so CEO sees all headlines
    console.log('📋 Available role boards for user:', {
      role: user.role,
      accessibleBoards: accessibleRoleBoards.map(b => b.id),
    });
  }, [user.role, user.isSuperAdmin, user.headlineAccess]);

  // Helper functions
  const toggleComments = (headlineId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(headlineId)) {
        newSet.delete(headlineId);
      } else {
        newSet.add(headlineId);
      }
      return newSet;
    });
  };

  const isLikedByUser = (headline: HeadlineItem) => {
    if (!user?.id) return false;
    return headline.likedBy?.includes(user.id) || false;
  };

  const getLikeTooltipContent = (headline: HeadlineItem) => {
    if (!headline.likedBy || headline.likedBy.length === 0) return null;
    const names = headline.likedBy.map(id => (id === user.id ? 'You' : (userIdToName[id] || 'User')));
    const max = 6;
    const visible = names.slice(0, max).join(', ');
    const more = names.length > max ? ` +${names.length - max} more` : '';
    return `${visible}${more}`;
  };

  // Attachments helpers
  const onPickFiles = (files: FileList | null) => {
    if (!files) return;
    setPostFiles(prev => [...prev, ...Array.from(files)]);
  };
  const removePostFile = (name: string) => setPostFiles(prev => prev.filter(f => f.name !== name));

  const addMentionToContent = (fullName: string, userId: string) => {
    // Insert "@Name" at caret
    const tag = `@${fullName}`;
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const newContent = content.substring(0, start) + tag + content.substring(end);
      setContent(newContent);
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.selectionStart = start + tag.length;
          contentRef.current.selectionEnd = start + tag.length;
          contentRef.current.focus();
        }
      }, 0);
    } else {
      setContent(prev => (prev + (prev.endsWith(' ') ? '' : ' ') + tag));
    }
    setPostMentions(prev => (prev.includes(userId) ? prev : [...prev, userId]));
    setMentionOpen(false);
    setMentionSearch('');
  };

  const addCommentMention = (headlineId: string, fullName: string, userId: string) => {
    const tag = `@${fullName}`;
    setCommentContent(prev => ({ ...prev, [headlineId]: `${prev[headlineId] || ''}${prev[headlineId]?.endsWith(' ') ? '' : ' '}${tag}` }));
    setCommentMentions(prev => ({
      ...prev,
      [headlineId]: prev[headlineId]?.includes(userId) ? prev[headlineId] : [ ...(prev[headlineId] || []), userId ]
    }));
    setCommentMentionOpen(null);
    setCommentMentionSearch('');
  };

  const onPickCommentFiles = (headlineId: string, files: FileList | null) => {
    if (!files) return;
    setCommentFiles(prev => ({
      ...prev,
      [headlineId]: [ ...(prev[headlineId] || []), ...Array.from(files) ]
    }));
  };
  const removeCommentFile = (headlineId: string, name: string) => {
    setCommentFiles(prev => ({
      ...prev,
      [headlineId]: (prev[headlineId] || []).filter(f => f.name !== name)
    }));
  };

  // NOW we can do early return checks AFTER all hooks
  if (!user) {
    console.error('Headlines: User is null or undefined');
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-neutral-500">User not authenticated. Please log in.</p>
      </div>
    );
  }

  // Ensure user has required properties
  if (!user.id) {
    console.error('Headlines: User missing ID', user);
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-neutral-500">User authentication error. Please log in again.</p>
      </div>
    );
  }
  
  // Get user accessible boards
  const userAccessibleBoards = useMemo(() => {
    const locationBoards = locations.map(loc => ({
      id: loc.id,
      name: loc.name,
      type: 'location' as const
    }));

    // Filter boards based on user access
    const accessibleLocationBoards = locationBoards.filter(board => {
      if (hasExecutiveLocationAccess(user.role, Boolean(user.isSuperAdmin))) {
        // Executive-level roles have access to all locations
        return true;
      }
      return user.locationIds?.includes(board.id) || false;
    });

    const roleBoards = [
      { id: ROLES.EXECUTIVE_TEAM, name: 'Executive Team', type: 'role' as const },
      { id: ROLES.TEAM_LEAD_OPTICAL, name: 'Team Lead Global', type: 'role' as const },
      { id: ROLES.TEAM_MEMBER, name: 'Team Member Global', type: 'role' as const },
      { id: 'BillingAuditing', name: 'Billing & Auditing', type: 'role' as const },
      { id: 'TrainingLevel', name: 'Training Level', type: 'role' as const },
      { id: 'Trainer', name: 'Trainer', type: 'role' as const },
      { id: ROLES.TASK_FORCE, name: 'Executive Taskforce', type: 'role' as const }
    ];

    // Role-based access logic
    const accessibleRoleBoards = roleBoards.filter(board => {
      if (hasExecutiveLocationAccess(user.role, Boolean(user.isSuperAdmin))) return true;
      
      // Map user roles to accessible boards
      const roleAccess: Record<string, string[]> = {
        [ROLES.TASK_FORCE]: [ROLES.TASK_FORCE, ROLES.TEAM_LEAD_OPTICAL, ROLES.TEAM_MEMBER],
        [ROLES.POINT_PEOPLE]: [ROLES.TEAM_LEAD_OPTICAL, ROLES.TEAM_MEMBER],
        [ROLES.TEAM_LEAD_DOCTOR]: [ROLES.TEAM_LEAD_OPTICAL, ROLES.TEAM_MEMBER],
        [ROLES.TEAM_LEAD_OPTICAL]: [ROLES.TEAM_LEAD_OPTICAL, ROLES.TEAM_MEMBER],
        [ROLES.TEAM_MEMBER]: [ROLES.TEAM_MEMBER]
      };

      const normalizedUserRole = normalizeRole(user.role);
      return normalizedUserRole ? roleAccess[normalizedUserRole]?.includes(board.id) || false : false;
    });

    console.log('📋 User Accessible Boards:', {
      location: accessibleLocationBoards,
      role: accessibleRoleBoards,
      userRole: user.role,
      selectedLocationIdsFromContext: selectedLocationIds
    });

    return { location: accessibleLocationBoards, role: accessibleRoleBoards };
  }, [locations, user.role, user.isSuperAdmin, user.locationIds, selectedLocationIds]);

  // Fix: When selectedLocationIds is empty (showing "All Locations"), 
  // we should use all accessible location IDs
  // Remove the incorrect logic that assumes all locations when CEO has 1 selected
  const actualLocationIds = selectedLocationIds.length === 0 
    ? userAccessibleBoards.location.map(board => board.id)
    : selectedLocationIds.filter(id => userAccessibleBoards.location.some(board => board.id === id));
  
  // Debug actualLocationIds calculation
  console.log('📊 actualLocationIds calculation:', {
    selectedLocationIds,
    selectedLocationIdsLength: selectedLocationIds.length,
    userRole: user.role,
    userAccessibleBoardsLocationLength: userAccessibleBoards.location.length,
    actualLocationIds,
    userAccessibleBoardsLocation: userAccessibleBoards.location
  });
  
  
  // Derive defaults synchronously: selecting the role tab needs only one query.
  const actualRoleBoards = selectedTab === 'role' && selectedRoleBoards.length === 0
    ? userAccessibleBoards.role.map(board => board.id).sort()
    : [...selectedRoleBoards].sort();

  // Fetch headlines
  const { data: headlinesData = [], isLoading: isLoadingHeadlines, isFetching: isFetchingHeadlines, error, refetch: refetchHeadlines } = useQuery<HeadlineItem[]>({
    // Include organizationId to isolate caches per org and prevent cross-tenant flicker
    queryKey: ['enhanced-headlines', currentOrganizationId, selectedTab, [...actualLocationIds].sort(), actualRoleBoards],
    queryFn: async () => {
      let url = '/api/enhanced-headlines';
      const params = new URLSearchParams();
      
      // Ensure backend uses the correct tenant override for super admin
      if (currentOrganizationId) {
        params.append('organizationId', currentOrganizationId);
      }

      // Always pass both filters - let the backend handle the logic
      // This ensures we get the right posts regardless of which tab we're on
      
      // Add location filter if any locations are selected
      if (actualLocationIds.length > 0) {
        params.append('locationIds', actualLocationIds.join(','));
      }
      
      // Add role filter if any role boards are selected
      if (actualRoleBoards.length > 0) {
        params.append('roleBoards', actualRoleBoards.join(','));
      }
      
      // Add tab context to help backend optimize the query
      params.append('tab', selectedTab);
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      logger.debug('Fetching enhanced headlines', { 
        url, 
        selectedTab, 
        actualLocationIds, 
        selectedRoleBoards 
      });
      
      console.log('📰 Headlines API Request:', {
        url,
        selectedTab,
        actualLocationIds,
        selectedRoleBoards,
        params: params.toString()
      });
      
      const response = await apiRequest<HeadlineItem[]>(url);
      console.log('📰 Headlines API Response:', {
        count: response.length,
        data: response,
        roleHeadlines: response.filter(h => h.targetRole),
        roleHeadlinesCount: response.filter(h => h.targetRole).length,
        locationHeadlines: response.filter(h => h.locationId || h.location_id),
        locationHeadlinesCount: response.filter(h => h.locationId || h.location_id).length
      });
      logLocationQuery('Headlines', actualLocationIds, response.length);
      
      return response;
    },
    enabled: locationsReady,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === currentOrganizationId
        ? keepPreviousData(previousData) : undefined,
    retry: false,
    staleTime: 0,
  });

  // Create headline mutation - Update to use enhanced API
  const createHeadline = useMutation({
    mutationFn: async (data: { 
      title: string; 
      content: string; 
      boardId: string;
      boardType: 'location' | 'role';
    }) => {
      logger.info('Creating headline', { 
        title: data.title, 
        boardId: data.boardId,
        boardType: data.boardType
      });
      
      const payload: any = {
        title: data.title,
        content: data.content
      };

      if (data.boardType === 'location') {
        payload.locationId = data.boardId;
      } else {
        // Map board ID to actual role name for backend
        payload.targetRole = BOARD_ID_TO_ROLE[data.boardId] || data.boardId;
      }
      
      if (!locationsReady) throw new Error('Wait for organization locations to load');
      if (data.boardType === 'location' && !locations.some(loc => loc.id === data.boardId)) {
        throw new Error('Choose a location in the selected organization');
      }
      return apiRequest(`/api/enhanced-headlines?organizationId=${encodeURIComponent(currentOrganizationId!)}`, {
        method: 'POST',
        data: payload
      });
    },
    onSuccess: async (data) => {
      console.log('✅ Post created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      setDialogOpen(false);
      setTitle("");
      setContent("");
      setSelectedBoard("");
      setBoardType('location');
      // Upload metadata for any selected files
      try {
        const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
        for (const f of postFiles) {
          const row = await apiRequest(`/api/attachments/upload${orgParam}`, {
            method: 'POST',
            data: {
              headlineId: data.id,
              fileName: f.name,
              mimeType: f.type || 'application/octet-stream',
              sizeBytes: f.size,
              kind: 'file'
            }
          });
          // Request a signed upload URL and PUT the file bytes so it persists and is downloadable by others
          const signed = await apiRequest(`/api/attachments/${row.id}/signed-upload${orgParam}`, { method: 'POST' });
          toast({ title: 'Uploading…', description: f.name });
          await fetch(signed.signedUpload.signedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': f.type || 'application/octet-stream', 'x-upsert': 'false' },
            body: f
          });
          toast({ title: 'Uploaded', description: f.name });
        }
        // Invalidate bulk map so persisted attachments appear
        queryClient.invalidateQueries({ queryKey: ['/api/attachments/bulk'] });
        await queryClient.refetchQueries({ queryKey: ['/api/attachments/bulk'] });
      } catch (e) {
        console.warn('Attachment metadata upload failed', e);
      } finally {
        // Keep local previews for this post
        setHeadlineLocalFiles(prev => ({ ...prev, [data.id]: postFiles }));
        setPostFiles([]);
      }
      // Create mentions for selected users
      try {
        if (postMentions.length > 0) {
          const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
          await apiRequest(`/api/mentions${orgParam}`, {
            method: 'POST',
            data: { headlineId: data.id, mentionedUserIds: postMentions }
          });
        }
      } catch (e) {
        console.warn('Mention creation failed', e);
      } finally {
        setPostMentions([]);
      }
      toast({
        title: "Success",
        description: "Your post has been published!",
      });
      
      // Log the current state to debug visibility
      console.log('📊 Current state after post creation:', {
        selectedTab,
        selectedRoleBoards,
        boardType: data.boardType,
        targetRole: data.targetRole
      });
    },
    onError: (error) => {
      logger.error('Failed to create headline', error);
      toast({
        title: "Error",
        description: "Failed to create post. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Handle creating a new post
  const handleCreatePost = () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both a title and content for your post.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedBoard) {
      toast({
        title: "Select a Board",
        description: "Please select which board to post to.",
        variant: "destructive",
      });
      return;
    }

    console.log('Creating post with:', { selectedBoard, boardType, title, content });
    
    createHeadline.mutate({
      title,
      content,
      boardId: selectedBoard,
      boardType
    });
  };

  // Add emoji to content
  const addEmoji = (emoji: string) => {
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const newContent = content.substring(0, start) + emoji + content.substring(end);
      setContent(newContent);
      
      // Reset cursor position after emoji insertion
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.selectionStart = start + emoji.length;
          contentRef.current.selectionEnd = start + emoji.length;
          contentRef.current.focus();
        }
      }, 0);
    }
    setShowEmojiPicker(false);
  };

  // Search for GIFs
  const searchGifs = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      // Show trending GIFs if no search term
      setGifResults(trendingGifs);
      return;
    } else {
      // Search for specific GIFs
      setLoadingGifs(true);
      try {
        const response = await fetch(
          `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(searchTerm)}&limit=30&rating=pg`
        );
        const data = await response.json();
        setGifResults(data.data || []);
      } catch (error) {
        logger.error('Failed to search GIFs', error);
        toast({
          title: "Error",
          description: "Failed to search GIFs. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoadingGifs(false);
      }
    }
  };

  // Add GIF to content
  const addGif = (gifUrl: string) => {
    const gifMarkdown = `\n![GIF](${gifUrl})\n`;
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const newContent = content.substring(0, start) + gifMarkdown + content.substring(end);
      setContent(newContent);
      
      // Reset cursor position after GIF insertion
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.selectionStart = start + gifMarkdown.length;
          contentRef.current.selectionEnd = start + gifMarkdown.length;
          contentRef.current.focus();
        }
      }, 0);
    }
    setShowGifPicker(false);
    setGifSearchTerm("");
    setGifResults([]);
  };

  // Get board info
  const getBoardInfo = (headline: HeadlineItem) => {
    // Check both locationId and location_id (API might return either)
    const headlineLocationId = headline.locationId || headline.location_id;
    if (headlineLocationId) {
      // Look up location from context locations
      const location = locations.find(loc => loc.id === headlineLocationId);
      return location ? { name: location.name, type: 'location' as const } : null;
    }
    if (headline.targetRole) {
      // Convert role name to board ID for lookup
      const normalizedTargetRole = normalizeRole(headline.targetRole);
      const boardId = ROLE_TO_BOARD_ID[normalizedTargetRole || headline.targetRole] || headline.targetRole;
      const board = ROLE_BOARDS.find(b => b.id === boardId);
      return board ? { name: board.name, type: 'role' as const } : null;
    }
    return null;
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Render content with GIFs
  const renderContent = (text: string) => {
    // Split content by GIF markdown pattern
    const parts = text.split(/(\!\[GIF\]\([^\)]+\))/g);
    
    return parts.map((part, index) => {
      // Check if this part is a GIF
      const gifMatch = part.match(/\!\[GIF\]\(([^\)]+)\)/);
      if (gifMatch) {
        const gifUrl = gifMatch[1];
        return (
          <img 
            key={index} 
            src={gifUrl} 
            alt="GIF" 
            className="my-2 rounded-lg max-w-full h-auto"
            style={{ maxHeight: '300px' }}
          />
        );
      }
      // Regular text
      return <span key={index} className="whitespace-pre-wrap">{part}</span>;
    });
  };

  // Filter headlines based on selected tab and permissions
  const filteredHeadlines = locationsReady ? headlinesData : [];
  
  // The backend now handles all filtering based on:
  // - selectedTab
  // - actualLocationIds  
  // - selectedRoleBoards
  // So we just use the data as returned
  
  console.log('📊 Headlines after backend filtering:', {
    total: filteredHeadlines.length,
    locationHeadlines: filteredHeadlines.filter(h => h.locationId || h.location_id).length,
    roleHeadlines: filteredHeadlines.filter(h => h.targetRole).length,
    selectedTab,
    actualLocationIds,
    selectedRoleBoards
  });
  
  // Sort headlines by date - newest first
  const sortedHeadlines = [...filteredHeadlines].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA; // Newest first
  });
  
  // Debug first headline to understand data structure
  if (sortedHeadlines.length > 0) {
    console.log('🔍 First headline data structure:', {
      id: sortedHeadlines[0].id,
      title: sortedHeadlines[0].title,
      likedBy: sortedHeadlines[0].likedBy,
      likeCount: sortedHeadlines[0].likeCount,
      commentCount: sortedHeadlines[0].commentCount,
      hasLikedBy: 'likedBy' in sortedHeadlines[0],
      hasLikeCount: 'likeCount' in sortedHeadlines[0],
      allFields: Object.keys(sortedHeadlines[0])
    });
  }
  
  // Debug logging
  console.log('Headlines data:', {
    totalHeadlines: headlinesData?.length || 0,
    filteredCount: sortedHeadlines.length,
    selectedTab,
    actualLocationIds,
    selectedRoleBoards,
    sampleHeadline: sortedHeadlines[0],
    isLoading: isLoadingHeadlines,
    queryError: error,
    // Check for shared posts
    sharedPosts: sortedHeadlines.filter(h => h.isShared),
    sharedPostsCount: sortedHeadlines.filter(h => h.isShared).length,
    // Debug role headlines
    roleHeadlines: headlinesData?.filter(h => h.targetRole) || [],
    roleHeadlinesCount: headlinesData?.filter(h => h.targetRole).length || 0,
    // Debug dates
    oldestPost: sortedHeadlines[sortedHeadlines.length - 1]?.createdAt,
    newestPost: sortedHeadlines[0]?.createdAt
  });

  // Contact the GIF provider only while a picker is showing trending results.
  const gifPickerOpen = showGifPicker || commentGifOpenId !== null;
  useEffect(() => {
    if (!gifPickerOpen || gifSearchTerm.trim() || trendingGifs.length > 0) return;
    const controller = new AbortController();
    let active = true;
    setLoadingGifs(true);
    const loadTrendingGifs = async () => {
      try {
        const response = await fetch(
          `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=12&rating=pg`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('GIF provider unavailable');
        const data = await response.json();
        if (!active) return;
        setTrendingGifs(data.data || []);
        setGifResults(data.data || []);
      } catch {
        if (active) setGifResults([]);
      } finally {
        if (active) setLoadingGifs(false);
      }
    };
    void loadTrendingGifs();
    return () => { active = false; controller.abort(); };
  }, [gifPickerOpen, gifSearchTerm, trendingGifs.length]);

  // Load comments when expanded
  useEffect(() => {
    const loadComments = async (headlineId: string) => {
      // Skip if already loading or already have data
      if (loadingComments[headlineId] || commentsData[headlineId]) return;
      
      setLoadingComments(prev => ({ ...prev, [headlineId]: true }));
      try {
        console.log('📚 Loading comments for headline:', headlineId);
        const comments = await apiRequest<Comment[]>(`/api/enhanced-headlines/${headlineId}/comments${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`);
        console.log('✅ Comments loaded:', { headlineId, count: comments.length, comments });
        setCommentsData(prev => ({ ...prev, [headlineId]: comments }));
      } catch (error) {
        console.error('❌ Failed to load comments:', error);
        toast({
          title: "Error",
          description: "Failed to load comments",
          variant: "destructive"
        });
      } finally {
        setLoadingComments(prev => ({ ...prev, [headlineId]: false }));
      }
    };
    
    // Load comments for all expanded headlines
    console.log('🔍 Expanded headlines:', Array.from(expandedComments));
    expandedComments.forEach(headlineId => {
      if (!commentsData[headlineId]) {
        loadComments(headlineId);
      }
    });
  }, [expandedComments, toast]); // Only depend on expandedComments and toast to prevent loops

  // Mutations for interactive features
  const likeHeadline = useMutation({
    mutationFn: async ({ headlineId, isLiked }: { headlineId: string; isLiked: boolean }) => {
      console.log('Like mutation called:', { headlineId, isLiked, userId: user?.id });
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      const endpoint = `/api/enhanced-headlines/${headlineId}/like${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`;
      const method = isLiked ? 'DELETE' : 'POST';
      
      console.log('Making API request:', { endpoint, method });
      
      if (isLiked) {
        return apiRequest(endpoint, {
          method: 'DELETE'
        });
      } else {
        return apiRequest(endpoint, {
          method: 'POST'
        });
      }
    },
    onSuccess: () => {
      console.log('Like mutation successful');
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
    },
    onError: (error: any) => {
      console.error('Like error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update like status",
        variant: "destructive"
      });
    }
  });

  const createComment = useMutation({
    mutationFn: async ({ headlineId, content, parentCommentId, files }: { 
      headlineId: string; 
      content: string; 
      parentCommentId?: string;
      files?: File[];
    }) => {
      console.log('📝 createComment mutation called:', {
        headlineId,
        content,
        parentCommentId,
        contentLength: content.length,
        fileCount: files?.length || 0
      });
      return apiRequest(`/api/enhanced-headlines/${headlineId}/comments${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`, {
        method: 'POST',
        data: { content, parentCommentId }
      });
    },
    onMutate: async ({ headlineId, content, parentCommentId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['enhanced-headlines'] });

      // Create optimistic comment
      const tempId = `temp-${Date.now()}`;
      const optimisticComment: Comment = {
        id: tempId,
        headlineId,
        userId: user.id,
        author: user.fullName,
        content,
        parentCommentId,
        createdAt: new Date(),
      };

      // Optimistically update the comments
      setCommentsData(prev => ({
        ...prev,
        [headlineId]: [...(prev[headlineId] || []), optimisticComment]
      }));

      // Return context for rollback
      return { 
        headlineId, 
        previousComments: commentsData[headlineId] || [],
        tempId 
      };
    },
    onSuccess: async (newComment, variables, context) => {
      // Replace the temporary comment with the real one
      if (context) {
        setCommentsData(prev => ({
          ...prev,
          [context.headlineId]: prev[context.headlineId].map(comment =>
            comment.id === context.tempId ? newComment : comment
          )
        }));
      }
      
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      setCommentContent(prev => ({ ...prev, [variables.headlineId]: '' }));
      // Upload attachment metadata for this comment
      try {
        const files = commentFiles[variables.headlineId] || [];
        if (files.length > 0) {
          const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
          for (const f of files) {
            const row = await apiRequest(`/api/attachments/upload${orgParam}`, {
              method: 'POST',
              data: {
                commentId: newComment.id,
                fileName: f.name,
                mimeType: f.type || 'application/octet-stream',
                sizeBytes: f.size,
                kind: 'file'
              }
            });
            const signed = await apiRequest(`/api/attachments/${row.id}/signed-upload${orgParam}`, { method: 'POST' });
            toast({ title: 'Uploading…', description: f.name });
            await fetch(signed.signedUpload.signedUrl, {
              method: 'PUT',
              headers: { 'Content-Type': f.type || 'application/octet-stream', 'x-upsert': 'false' },
              body: f
            });
            toast({ title: 'Uploaded', description: f.name });
          }
          // Refresh bulk data for current viewport
          queryClient.invalidateQueries({ queryKey: ['/api/attachments/bulk'] });
          await queryClient.refetchQueries({ queryKey: ['/api/attachments/bulk'] });
        }
      } catch (e) {
        console.warn('Comment attachments upload failed', e);
      } finally {
        setCommentLocalFiles(prev => ({ ...prev, [newComment.id]: (commentFiles[variables.headlineId] || []) }));
        setCommentFiles(prev => ({ ...prev, [variables.headlineId]: [] }));
      }
      // Create mentions for this comment
      try {
        const mentions = commentMentions[variables.headlineId] || [];
        if (mentions.length > 0) {
          const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
          await apiRequest(`/api/mentions${orgParam}`, {
            method: 'POST',
            data: { commentId: newComment.id, mentionedUserIds: mentions }
          });
        }
      } catch (e) {
        console.warn('Comment mentions create failed', e);
      } finally {
        setCommentMentions(prev => ({ ...prev, [variables.headlineId]: [] }));
      }
      if (variables.parentCommentId) {
        setReplyingTo(prev => {
          const newState = { ...prev };
          if (variables.parentCommentId) {
            delete newState[variables.parentCommentId];
          }
          return newState;
        });
      }
    },
    onError: (error: any, variables, context) => {
      // Rollback on error
      if (context) {
        setCommentsData(prev => ({
          ...prev,
          [context.headlineId]: context.previousComments
        }));
      }
      
      console.error('Comment error:', error);
      console.error('Comment error details:', {
        message: error.message,
        response: error.response,
        status: error.status,
        data: error.data
      });
      toast({
        title: "Error",
        description: error.message || "Failed to post comment. Please try again.",
        variant: "destructive",
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={() => createComment.mutate(variables)}
          >
            Retry
          </Button>
        )
      });
    }
  });

  const updateComment = useMutation({
    mutationFn: async ({ commentId, content }: { commentId: string; content: string }) => {
      console.log('📝 Updating comment:', { commentId, content });
      try {
        const result = await apiRequest(`/api/comments/${commentId}${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`, {
          method: 'PATCH',
          data: { content }
        });
        console.log('✅ Comment updated:', result);
        return result;
      } catch (error: any) {
        console.error('❌ API Error:', {
          message: error.message,
          response: error.response,
          data: error.data,
          status: error.status
        });
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      console.log('✅ Update successful, refreshing comments...');
      // Refresh all comments since we don't know which headline this comment belongs to
      expandedComments.forEach(async (headlineId) => {
        try {
          const comments = await apiRequest<Comment[]>(`/api/enhanced-headlines/${headlineId}/comments${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`);
          setCommentsData(prev => ({ ...prev, [headlineId]: comments }));
        } catch (error) {
          console.error('Failed to refresh comments:', error);
        }
      });
      setEditingComment(null);
      setCommentContent({});  // Clear the edit content
      toast({
        title: "Success",
        description: "Comment updated successfully"
      });
    },
    onError: (error: any) => {
      console.error('❌ Failed to update comment - Full error:', {
        error,
        message: error.message,
        response: error.response,
        data: error.data
      });
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          "Failed to update comment";
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      console.log('🗑️ Deleting comment:', commentId);
      const result = await apiRequest(`/api/comments/${commentId}${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`, {
        method: 'DELETE'
      });
      console.log('✅ Comment deleted successfully');
      return result;
    },
    onSuccess: () => {
      // Refresh all comments since we don't know which headline this comment belongs to
      expandedComments.forEach(async (headlineId) => {
        try {
          const comments = await apiRequest<Comment[]>(`/api/enhanced-headlines/${headlineId}/comments${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`);
          setCommentsData(prev => ({ ...prev, [headlineId]: comments }));
        } catch (error) {
          console.error('Failed to refresh comments:', error);
        }
      });
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      toast({
        title: "Success",
        description: "Comment deleted successfully"
      });
    },
    onError: (error: any) => {
      console.error('❌ Failed to delete comment:', error);
      toast({
        title: "Error", 
        description: error.message || "Failed to delete comment",
        variant: "destructive"
      });
    }
  });

  const shareHeadline = useMutation({
    mutationFn: async ({ 
      headlineId, 
      toBoardId, 
      toBoardType, 
      toBoardName, 
      additionalMessage 
    }: {
      headlineId: string;
      toBoardId: string;
      toBoardType: 'location' | 'role';
      toBoardName: string;
      additionalMessage?: string;
    }) => {
      return apiRequest(`/api/enhanced-headlines/${headlineId}/share${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`, {
        method: 'POST',
        data: { 
          targetBoardId: toBoardId,     // Backend expects targetBoardId
          targetBoardType: toBoardType, // Backend expects targetBoardType
          targetBoardName: toBoardName, // Backend expects targetBoardName
          additionalMessage 
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      toast({
        title: "Post shared successfully!",
        description: "Your post has been shared to the selected board."
      });
      setShareDialogOpen(false);
      setSharingPost(null);
      setShareMessage('');
      setSelectedShareBoard('');
    },
    onError: (error: any) => {
      console.error('Share error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to share post",
        variant: "destructive"
      });
    }
  });

  const updateHeadline = useMutation({
    mutationFn: async ({ headlineId, title, content }: { 
      headlineId: string; 
      title: string; 
      content: string 
    }) => {
      return apiRequest(`/api/enhanced-headlines/${headlineId}${currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : ""}`, {
        method: 'PUT',
        data: { title, content }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      setEditingPost(null);
      toast({
        title: "Post updated",
        description: "Your post has been updated successfully."
      });
    }
  });

  const deleteHeadline = useMutation({
    mutationFn: async ({ headlineId, force }: { headlineId: string; force?: boolean }) => {
      const params = new URLSearchParams();
      if (force) params.append('force', 'true');
      if (currentOrganizationId) params.append('organizationId', currentOrganizationId);
      
      return apiRequest(`/api/enhanced-headlines/${headlineId}${params.toString() ? '?' + params.toString() : ''}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] });
      toast({
        title: "Post deleted",
        description: "Your post has been deleted successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Cannot delete post",
        description: error.message || "Posts with comments cannot be deleted.",
        variant: "destructive"
      });
    }
  });

  // Render content with GIFs
  const renderComments = (comments: Comment[], parentId?: string | null, depth: number = 0) => {
    if (depth > 2) return null; // Max nesting depth
    
    // Fix: Handle both undefined and null for top-level comments
    const filteredComments = comments.filter(c => {
      if (!parentId) {
        // For top-level comments, match both null and undefined
        return !c.parentCommentId || c.parentCommentId === null;
      }
      return c.parentCommentId === parentId;
    });
    
    return filteredComments.map(comment => {
      const isOptimistic = comment.id.startsWith('temp-');
      
      return (
        <div key={comment.id} className={`${depth > 0 ? 'ml-6' : ''} mb-2 ${isOptimistic ? 'opacity-70' : ''}`}>
          <div className="flex items-start space-x-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${comment.author}`} />
              <AvatarFallback className="text-xs">{getInitials(comment.author)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-semibold text-xs">{comment.author}</span>
                  <div className="flex items-center space-x-1">
                    {isOptimistic && (
                      <span className="text-xs text-neutral-500 italic">Posting...</span>
                    )}
                    <span className="text-xs text-neutral-500">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                </div>
                {editingComment === comment.id ? (
                  <div className="space-y-1">
                    <Textarea
                      value={commentContent[comment.id] || comment.content}
                      onChange={(e) => setCommentContent(prev => ({ 
                        ...prev, 
                        [comment.id]: e.target.value 
                      }))}
                      className="text-xs"
                      rows={2}
                    />
                    <div className="flex space-x-1">
                      <Button 
                        size="sm" 
                        className="h-6 text-xs px-2"
                        onClick={() => {
                          const newContent = commentContent[comment.id] || comment.content;
                          console.log('💾 Save button clicked:', {
                            commentId: comment.id,
                            originalContent: comment.content,
                            newContent,
                            hasChanged: newContent !== comment.content
                          });
                          updateComment.mutate({
                            commentId: comment.id,
                            content: newContent
                          });
                        }}
                        disabled={updateComment.isPending}
                      >
                        {updateComment.isPending ? 'Saving...' : 'Save'}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-6 text-xs px-2"
                        onClick={() => {
                          setEditingComment(null);
                          setCommentContent(prev => {
                            const newState = { ...prev };
                            delete newState[comment.id];
                            return newState;
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Check if content is a GIF - either markdown format or direct URL */}
                    {(() => {
                      // Check for markdown GIF format ![GIF](url)
                      const markdownMatch = comment.content.match(/^!\[GIF\]\((.*?)\)$/);
                      if (markdownMatch) {
                        return (
                          <img 
                            src={markdownMatch[1]} 
                            alt="GIF" 
                            className="max-w-xs rounded-lg mt-1"
                            loading="lazy"
                          />
                        );
                      }
                      // Check for direct Giphy URL or .gif/.webp files
                      if (comment.content.match(/^https?:\/\/.*giphy\.com\/.*$/i) || 
                          comment.content.match(/^https?:\/\/.*\.(gif|webp)(\?.*)?$/i)) {
                        return (
                          <img 
                            src={comment.content} 
                            alt="GIF" 
                            className="max-w-xs rounded-lg mt-1"
                            loading="lazy"
                          />
                        );
                      }
                      // Regular text content
                      return <p className="text-xs">{comment.content}</p>;
                    })()}
                    {comment.editedAt && (
                      <span className="text-xs text-neutral-500 italic">(edited)</span>
                    )}
                  </>
                )}
              </div>
              {/* Action buttons: Reply, Edit, Delete */}
              <div className="flex items-center space-x-1 mt-0.5">
                {depth < 2 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-5 px-1"
                    onClick={() => setReplyingTo(prev => ({ ...prev, [comment.id]: '' }))}
                  >
                    Reply
                  </Button>
                )}
                
                {!isOptimistic && (user?.isSuperAdmin === true ||
                  (Boolean(user?.id) && comment.userId === user?.id)) && (
                  <>
                    {/* Edit own comments or moderate as super admin */}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 px-1 text-xs text-purple-600 hover:text-purple-700"
                      onClick={() => {
                        const isAuthor = Boolean(user?.id) && comment.userId === user?.id;
                        const isSuperAdmin = user?.isSuperAdmin === true;
                        
                        if (isAuthor || isSuperAdmin) {
                          setEditingComment(comment.id);
                        } else {
                          toast({
                            title: "Permission Denied",
                            description: "You can only edit your own comments.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <span className="mdi mdi-pencil text-xs"></span>
                      Edit
                    </Button>
                    
                    {/* Delete own comments or moderate as super admin */}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 px-1 text-xs text-red-600 hover:text-red-700"
                      onClick={() => {
                        const isAuthor = Boolean(user?.id) && comment.userId === user?.id;
                        const isSuperAdmin = user?.isSuperAdmin === true;
                        
                        if (isAuthor || isSuperAdmin) {
                          if (confirm('Are you sure you want to delete this comment?')) {
                            deleteComment.mutate(comment.id);
                          }
                        } else {
                          toast({
                            title: "Permission Denied",
                            description: "You can only delete your own comments.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <span className="mdi mdi-delete text-xs"></span>
                      Delete
                    </Button>
                  </>
                )}
              </div>
              
              {replyingTo[comment.id] !== undefined && (
                <div className="mt-1 space-y-1">
                  <Textarea
                    value={replyingTo[comment.id]}
                    onChange={(e) => setReplyingTo(prev => ({ 
                      ...prev, 
                      [comment.id]: e.target.value 
                    }))}
                    placeholder="Write a reply..."
                    className="text-xs"
                    rows={2}
                  />
                  <div className="flex space-x-1">
                    <Button 
                      size="sm"
                      className="h-6 text-xs px-2"
                      onClick={() => {
                        createComment.mutate({
                          headlineId: comment.headlineId,
                          content: replyingTo[comment.id],
                          parentCommentId: comment.id
                        });
                      }}
                      disabled={!replyingTo[comment.id].trim()}
                    >
                      Reply
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="h-6 text-xs px-2"
                      onClick={() => setReplyingTo(prev => {
                        const newState = { ...prev };
                        delete newState[comment.id];
                        return newState;
                      })}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Render nested comments */}
              {renderComments(comments, comment.id, depth + 1)}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {/* Sticky Header Section - Everything that should stick together */}
      <div data-testid="headlines-header" className="sticky top-0 z-20 bg-neutral-900 min-w-0">
        {/* Headlines Section */}
        <div className="px-6 py-2 bg-neutral-900">
          {/* Headlines Card and Gray Box in same row */}
          <div className="flex flex-col sm:flex-row min-w-0 items-start gap-4">
            {/* Headlines Card */}
            <div className="bg-neutral-800 dark:bg-neutral-900 p-3 rounded-lg border border-neutral-700 w-full sm:w-64 sm:shrink-0">
              <h1 className="text-2xl font-bold text-white">Headlines</h1>
              <p className="text-neutral-400 text-sm mt-1">
                Share updates, announcements<br />
                and collaborate with your team
              </p>
            </div>

            {/* Keep long names inside the filter summary on narrow screens. */}
            <div data-testid="headline-filter-summary" className="bg-neutral-800 dark:bg-neutral-900 text-neutral-300 px-3 py-2 rounded-lg border border-neutral-700 text-sm min-w-0 max-w-full w-full sm:flex-1 overflow-hidden">
              <div className="line-clamp-2 break-words" title={locations.filter(loc => actualLocationIds.includes(loc.id)).map(loc => loc.name).join(', ')}>
                Locations: {actualLocationIds.length === userAccessibleBoards.location.length ? 'All ' : ''}({actualLocationIds.length})
              </div>
              <div className="mt-1 line-clamp-2 break-words" title={userAccessibleBoards.role.filter(board => actualRoleBoards.includes(board.id)).map(board => board.name).join(', ')}>
                Roles: {actualRoleBoards.length === userAccessibleBoards.role.length ? 'All ' : ''}({actualRoleBoards.length})
              </div>
            </div>
          </div>

          {/* Toolbar - Tabs + Role Board Selector + New Post */}
          <div className="flex flex-col xl:flex-row xl:items-center gap-2 mt-2 min-w-0">
            {/* Tabs */}
            <Tabs className="w-full xl:w-auto min-w-0" value={selectedTab} onValueChange={(v) => {
              console.log('🔄 Tab changed to:', v, {
                selectedRoleBoards,
                actualLocationIds,
                currentHeadlines: headlinesData?.length || 0
              });
              setSelectedTab(v as 'all' | 'location' | 'role');
            }}>
              <TabsList className="bg-neutral-200 dark:bg-neutral-700 h-10 grid grid-cols-3 w-full">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-neutral-900 data-[state=active]:text-white"
                >
                  <span className="sm:hidden">All</span><span className="hidden sm:inline">All Posts</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="location" 
                  className="data-[state=active]:bg-neutral-900 data-[state=active]:text-white"
                >
                  <span className="sm:hidden">Locations</span><span className="hidden sm:inline">Location Boards</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="role" 
                  className="data-[state=active]:bg-neutral-900 data-[state=active]:text-white"
                >
                  <span className="sm:hidden">Roles</span><span className="hidden sm:inline">Role Boards</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Right side: Role Board Selector + Global Comment Toggle + New Post */}
            <div className="flex flex-wrap items-center gap-2 min-w-0 xl:ml-auto">
              <div className="w-full min-w-0 xl:w-56">
                {selectedTab === 'location' ? (
                  <BoardSelector
                    boards={userAccessibleBoards.location}
                    selectedIds={actualLocationIds}
                    onSelectionChange={setSelectedLocationIds}
                    placeholder="Select location boards"
                    className="w-full min-w-0 h-10 text-sm"
                  />
                ) : (
                  <BoardSelector
                    boards={userAccessibleBoards.role}
                    selectedIds={actualRoleBoards}
                    onSelectionChange={setSelectedRoleBoards}
                    placeholder="All Role Based Boards"
                    className="w-full min-w-0 h-10 text-sm"
                    showSelectAll={true}
                  />
                )}
              </div>
              {/* Global comments toggle */}
              <Button
                variant="outline"
                className="h-10 text-sm"
                onClick={() => {
                  const allIds = (headlinesData || []).map(h => h.id);
                  const isAllOpen = allIds.every(id => expandedComments.has(id));
                  if (isAllOpen) {
                    setExpandedComments(new Set());
                  } else {
                    setExpandedComments(new Set(allIds));
                  }
                }}
              >
                {(() => {
                  const allIds = (headlinesData || []).map(h => h.id);
                  const isAllOpen = allIds.length > 0 && allIds.every(id => expandedComments.has(id));
                  return isAllOpen ? 'Hide all comments' : 'View all comments';
                })()}
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="default" className="h-10">
                    <span className="mdi mdi-pencil mr-2"></span>
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Create New Post</DialogTitle>
                    <DialogDescription>
                      Share an update with your team. You can add emojis and GIFs to make it more engaging!
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="grid gap-4 py-4 overflow-y-auto flex-1">
                    {/* Board Selection */}
                    <div className="grid gap-2">
                      <Label>Select Board</Label>
                      <Tabs value={boardType} onValueChange={(v) => setBoardType(v as 'location' | 'role')}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="location">Location Boards</TabsTrigger>
                          <TabsTrigger value="role">Role Boards</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="location" className="mt-4">
                          <div className="grid grid-cols-2 gap-2">
                            {userAccessibleBoards.location.map(board => (
                              <Button
                                key={board.id}
                                variant={selectedBoard === board.id && boardType === 'location' ? 'default' : 'outline'}
                                className="justify-start"
                                onClick={() => setSelectedBoard(board.id)}
                              >
                                {board.name}
                              </Button>
                            ))}
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="role" className="mt-4">
                          <div className="grid grid-cols-1 gap-2">
                            {userAccessibleBoards.role.map(board => (
                              <Button
                                key={board.id}
                                variant={selectedBoard === board.id && boardType === 'role' ? 'default' : 'outline'}
                                className="justify-start"
                                onClick={() => setSelectedBoard(board.id)}
                              >
                                {board.name}
                              </Button>
                            ))}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                    
                    {/* Title */}
                    <div className="grid gap-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="What's your post about?"
                        className="text-lg"
                      />
                    </div>
                    
                    {/* Content with emoji/gif buttons */}
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="content">Content</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 text-xs">
                                <span className="mdi mdi-emoticon-happy-outline mr-1"></span>
                                Emoji
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80">
                              <div className="grid grid-cols-8 gap-2">
                                {POPULAR_EMOJIS.map(emoji => (
                                  <Button
                                    key={emoji}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => addEmoji(emoji)}
                                  >
                                    {emoji}
                                  </Button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          
                          <Popover open={showGifPicker} onOpenChange={(open) => {
                            setShowGifPicker(open);
                            if (open) {
                              // Reset to trending when opening
                              setGifSearchTerm('');
                              setGifResults(trendingGifs);
                            }
                          }}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 text-xs">
                                <span className="mdi mdi-gif mr-1"></span>
                                GIF
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[480px] p-0" align="end" sideOffset={5}>
                              <div className="flex flex-col h-[600px]">
                                {/* Fixed header with search */}
                                <div className="p-3 border-b bg-white dark:bg-neutral-900 flex-shrink-0">
                                  <Input
                                    placeholder="Search for GIFs..."
                                    value={gifSearchTerm}
                                    onChange={(e) => {
                                      setGifSearchTerm(e.target.value);
                                      // Search GIFs as user types
                                      if (e.target.value.trim()) {
                                        searchGifs(e.target.value);
                                      } else {
                                        // Show trending when search is cleared
                                        setGifResults(trendingGifs);
                                      }
                                    }}
                                    className="w-full"
                                    autoFocus
                                  />
                                </div>
                                
                                {/* Scrollable content area - flex-1 to take remaining space */}
                                <div 
                                  className="flex-1 overflow-y-auto p-2 scroll-smooth"
                                  style={{ scrollBehavior: 'smooth' }}
                                  ref={gifScrollRef}
                                >
                                  {loadingGifs ? (
                                    <div className="flex items-center justify-center h-full">
                                      <div className="text-center">
                                        <span className="mdi mdi-loading mdi-spin text-2xl"></span>
                                        <p className="text-sm text-neutral-500 mt-2">Loading GIFs...</p>
                                      </div>
                                    </div>
                                  ) : gifResults.length > 0 ? (
                                    <div className="grid grid-cols-3 gap-2">
                                      {gifResults.map((gif) => (
                                        <button
                                          key={gif.id}
                                          onClick={() => addGif(gif.images.fixed_height.url)}
                                          className="relative group overflow-hidden rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 aspect-square"
                                        >
                                          <img
                                            src={gif.images.fixed_height_small.url}
                                            alt={gif.title}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                          />
                                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center h-full text-center p-4">
                                      <div>
                                        <span className="mdi mdi-magnify text-3xl text-neutral-400"></span>
                                        <p className="text-sm text-neutral-500 mt-2">Search for GIFs or browse trending</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>

                          {/* Attach */}
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => onPickFiles(e.target.files)}
                            />
                            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => fileInputRef.current?.click()}>
                              <span className="mdi mdi-paperclip mr-1"></span>
                              Attach
                            </Button>
                            {/* In-modal selected files indicator */}
                            {(postFiles.length > 0) && (
                              <span className="ml-2 text-xs text-neutral-500">
                                {postFiles.length} file{postFiles.length > 1 ? 's' : ''} selected
                              </span>
                            )}
                          </div>

                          {/* @ Mention */}
                          <Popover open={mentionOpen} onOpenChange={setMentionOpen}>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 text-xs">
                                <span className="mdi mdi-at mr-1"></span>
                                Mention
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="end">
                              <Input
                                placeholder="Search people..."
                                value={mentionSearch}
                                onChange={(e) => setMentionSearch(e.target.value)}
                                className="mb-2 h-8 text-xs"
                              />
                              <div className="max-h-56 overflow-auto space-y-1">
                                {(usersForOrg as any[])
                                  .filter(u => (u.fullName || u.full_name || '').toLowerCase().includes(mentionSearch.toLowerCase()))
                                  .slice(0, 20)
                                  .map(u => (
                                    <Button key={u.id} variant="ghost" size="sm" className="w-full justify-start h-8 text-xs"
                                      onClick={() => addMentionToContent(u.fullName || u.full_name || u.email || 'User', u.id)}>
                                      <span className="mdi mdi-account-circle-outline mr-2"></span>
                                      {u.fullName || u.full_name || u.email}
                                    </Button>
                                  ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <Textarea
                        id="content"
                        ref={contentRef}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Share your thoughts, updates, or announcements..."
                        rows={6}
                        className="text-base"
                      />
                    </div>
                    {/* In-modal thumbnails for selected files */}
                    {(postFiles.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {postFiles.map((f) => {
                          const isImage = (f.type || '').startsWith('image/');
                          const url = URL.createObjectURL(f);
                          return (
                            <div key={f.name} className="relative">
                              {isImage ? (
                                <img src={url} alt={f.name} className="h-20 w-20 object-cover rounded" />
                              ) : (
                                <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={() => window.open(url, '_blank')}>
                                  <span className="mdi mdi-paperclip mr-1" /> {f.name}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreatePost}
                      disabled={!locationsReady || createHeadline.isPending || !selectedBoard || !title.trim() || !content.trim()}
                    >
                      {createHeadline.isPending ? "Posting..." : "Share BOLT Culture"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-4 space-y-4">
          {/* Posts List */}
          <div className="space-y-3">
            {isFetchingHeadlines && headlinesData.length > 0 && locationsReady && (
              <div role="progressbar" aria-label="Updating posts" className="h-1 w-full animate-pulse bg-blue-500 rounded" />
            )}
            {error || locationsError ? (
              <div role="alert" className="rounded border border-red-300 bg-red-50 p-4 text-red-900">
                Unable to load posts. Please reload or try again.
                <Button variant="outline" onClick={() => refetchHeadlines()} disabled={!locationsReady}>Try again</Button>
              </div>
            ) : !locationsReady || isLoadingHeadlines ? (
              [...Array(4)].map((_, index) => (
                <Skeleton key={index} className="h-40 w-full" />
              ))
            ) : sortedHeadlines.length > 0 ? (
              sortedHeadlines.map((headline) => {
                const boardInfo = getBoardInfo(headline);
                
                return (
                  <Card key={headline.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2 pt-3 px-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${headline.author}`} />
                            <AvatarFallback className="text-xs">{getInitials(headline.author)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center space-x-1">
                              <h3 className="font-semibold text-sm">{headline.author}</h3>
                              <span className="text-xs text-neutral-500">•</span>
                              <span className="text-xs text-neutral-500">
                                {formatDate(headline.createdAt)}
                                {headline.createdAt && (
                                  <> at {new Date(headline.createdAt).toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}</>
                                )}
                              </span>
                              {headline.lastEditedAt && (
                                <span className="text-xs text-neutral-500 italic">(edited)</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-1 mt-0.5">
                              {boardInfo && (
                                <Badge variant={boardInfo.type === 'location' ? 'default' : 'secondary'} className="bg-blue-600 text-white text-xs py-0 px-1.5">
                                  {boardInfo.name}
                                </Badge>
                              )}
                              {headline.isShared && headline.sharedFrom && (
                                <Badge className="bg-orange-500 text-white text-xs py-0 px-1.5">
                                  Shared from {headline.sharedFrom.boardName}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {headline.authorId === user?.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <span className="mdi mdi-dots-vertical text-sm"></span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setEditingPost(headline.id);
                                // Set initial values for editing
                                setTitle(headline.title);
                                setContent(headline.content);
                              }}>
                                <span className="mdi mdi-pencil mr-2"></span>
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600">
                                {/* RBAC-aware delete */}
                                <DeleteButton
                                  contentType={'headlines'}
                                  contentId={headline.id}
                                  organizationId={currentOrganizationId || undefined}
                                  hasComments={(headline as any)?.commentCount > 0}
                                  label={'Delete'}
                                  onDeleted={() => queryClient.invalidateQueries({ queryKey: ['enhanced-headlines'] })}
                                />
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      {headline.isShared && headline.sharedFrom?.originalAuthor && (
                        <div className="text-xs text-neutral-500 mt-1 ml-10">
                          Originally posted by {headline.sharedFrom.originalAuthor}
                        </div>
                      )}
                    </CardHeader>
                    
                    <CardContent className="px-3 py-2">
                      {editingPost === headline.id ? (
                        <div className="space-y-2">
                          <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Title"
                            className="text-sm"
                          />
                          <Textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Content"
                            className="text-sm"
                            rows={4}
                          />
                          <div className="flex space-x-2">
                            <Button 
                              size="sm"
                              onClick={() => {
                                updateHeadline.mutate({
                                  headlineId: headline.id,
                                  title,
                                  content
                                });
                              }}
                              disabled={!title.trim() || !content.trim()}
                            >
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setEditingPost(null);
                                setTitle('');
                                setContent('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h4 className="text-base font-semibold mb-1">{headline.title}</h4>
                          <div className="text-sm">{renderContent(headline.content)}</div>
                          {/* Persisted attachments (fetched) */}
                          <PersistedAttachments headlineId={headline.id} organizationId={currentOrganizationId ?? undefined} />
                          {(headlineLocalFiles[headline.id]?.length || 0) > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(headlineLocalFiles[headline.id] || []).map((f) => {
                                const isImage = (f.type || '').startsWith('image/');
                                const url = URL.createObjectURL(f);
                                return (
                                  <div key={f.name} className="relative">
                                    {isImage ? (
                                      <img src={url} alt={f.name} className="h-28 w-28 object-cover rounded cursor-pointer"
                                        onClick={() => setPreviewUrl(url)} />
                                    ) : (
                                      <Button variant="secondary" size="sm" className="h-8 text-xs" onClick={() => window.open(url, '_blank') }>
                                        <span className="mdi mdi-paperclip mr-1" /> {f.name}
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                    
                    <CardFooter className="pt-2 pb-1 px-3 border-t">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className={`h-7 px-2 text-xs ${isLikedByUser(headline) ? 'text-green-500 hover:text-green-600' : 'hover:text-gray-600'}`}
                                  onClick={() => {
                                    console.log('Like button clicked:', {
                                      headlineId: headline.id,
                                      currentlyLiked: isLikedByUser(headline),
                                      userId: user?.id,
                                      likedBy: headline.likedBy
                                    });
                                    likeHeadline.mutate({ 
                                      headlineId: headline.id, 
                                      isLiked: isLikedByUser(headline) 
                                    });
                                  }}
                                >
                                  <span className={`mdi ${isLikedByUser(headline) ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'} mr-1`}>
                                    {/* Fallback text if icon doesn't load */}
                                    <span className="sr-only">Like</span>
                                  </span>
                                  {headline.likeCount > 0 && headline.likeCount}
                                </Button>
                              </TooltipTrigger>
                                <TooltipContent>
                                <p className="text-xs">
                                  {headline.likedBy && headline.likedBy.length > 0 
                                    ? getLikeTooltipContent(headline)
                                    : 'Be the first to like this'}
                                </p>
                                </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-xs"
                            onClick={() => toggleComments(headline.id)}
                          >
                            <span className="mdi mdi-comment-outline mr-1"></span>
                            {expandedComments.has(headline.id) ? (
                              'Hide comments'
                            ) : headline.commentCount > 0 ? (
                              `View ${headline.commentCount} comment${headline.commentCount > 1 ? 's' : ''}`
                            ) : (
                              'Comment'
                            )}
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-xs text-orange-500 hover:text-orange-600"
                            onClick={() => {
                              setSharingPost(headline);
                              setShareDialogOpen(true);
                            }}
                          >
                            <span className="mdi mdi-share-outline mr-1"></span>
                            Share
                          </Button>
                          
                          {/* Editing follows the API's author/super-admin boundary. */}
                          {(user?.isSuperAdmin === true ||
                            (Boolean(user?.id) && headline.authorId === user?.id)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-purple-600 hover:text-purple-700"
                              onClick={() => {
                                setEditingHeadline(headline);
                                setEditHeadlineContent({
                                  title: headline.title,
                                  content: headline.content
                                });
                                setEditDialogOpen(true);
                              }}
                            >
                              <span className="mdi mdi-pencil mr-1"></span>
                              Edit
                            </Button>
                          )}
                          
                          {(user?.isSuperAdmin === true ||
                            (Boolean(user?.id) && headline.authorId === user?.id && headline.commentCount === 0)) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
                            onClick={() => {
                              // Check if user can delete
                              const isAuthor = headline.authorId === user?.id;
                              const isSuperAdmin = user?.isSuperAdmin === true;
                              const hasComments = headline.commentCount > 0;
                              
                              if (isSuperAdmin || (isAuthor && !hasComments)) {
                                if (confirm(`Are you sure you want to delete this post${hasComments ? ' and all its comments' : ''}?`)) {
                                  // Call delete API with force flag for super admins with comments
                                  deleteHeadline.mutate({ 
                                    headlineId: headline.id, 
                                    force: isSuperAdmin && hasComments
                                  });
                                }
                              } else if (isAuthor && hasComments) {
                                toast({
                                  title: "Cannot Delete",
                                  description: "You cannot delete posts that have comments.",
                                  variant: "destructive",
                                });
                              } else {
                                toast({
                                  title: "Permission Denied",
                                  description: "You can only delete your own posts.",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <span className="mdi mdi-delete mr-1"></span>
                            Delete
                          </Button>
                          )}
                        </div>
                      </div>
                    </CardFooter>
                    
                    {/* Comments Section */}
                    {expandedComments.has(headline.id) && (
                      <div className="border-t px-3 py-2 bg-neutral-50 dark:bg-neutral-900">
                        {/* Comment Input */}
                        <div className="flex space-x-2 mb-3">
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${user.fullName}`} />
                            <AvatarFallback className="text-xs">{getInitials(user.fullName)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Textarea
                              value={commentContent[headline.id] || ''}
                              onChange={(e) => setCommentContent(prev => ({ 
                                ...prev, 
                                [headline.id]: e.target.value 
                              }))}
                              placeholder="Write a comment..."
                              className="text-sm"
                            />
                            <div className="flex items-center gap-2 mt-1">
                              {/* Emoji */}
                              <Popover open={commentEmojiOpenId === headline.id} onOpenChange={(o) => setCommentEmojiOpenId(o ? headline.id : null)}>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8"><span className="mdi mdi-emoticon-happy-outline mr-1" /> Emoji</Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72">
                                  <div className="grid grid-cols-8 gap-2">
                                    {POPULAR_EMOJIS.map(emoji => (
                                      <Button key={emoji} variant="ghost" size="sm" className="h-8 w-8 p-0"
                                        onClick={() => setCommentContent(prev => ({ ...prev, [headline.id]: `${(prev[headline.id] || '')}${emoji}` }))}>
                                        {emoji}
                                      </Button>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>

                              {/* GIF */}
                              <Popover open={commentGifOpenId === headline.id} onOpenChange={(o) => {
                                setCommentGifOpenId(o ? headline.id : null);
                                if (o) {
                                  // Reset to trending when opening
                                  setGifSearchTerm('');
                                  setGifResults(trendingGifs);
                                }
                              }}>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8"><span className="mdi mdi-gif mr-1" /> GIF</Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[480px] p-0" align="start" side="top" sideOffset={5}>
                                  <div className="flex flex-col h-[400px]">
                                    {/* Fixed header with search */}
                                    <div className="p-3 border-b bg-white dark:bg-neutral-900 flex-shrink-0">
                                      <Input 
                                        placeholder="Search for GIFs..." 
                                        value={gifSearchTerm} 
                                        onChange={(e) => {
                                          setGifSearchTerm(e.target.value);
                                          // Search GIFs as user types
                                          if (e.target.value.trim()) {
                                            searchGifs(e.target.value);
                                          } else {
                                            // Show trending when search is cleared
                                            setGifResults(trendingGifs);
                                          }
                                        }}
                                        className="w-full"
                                        autoFocus
                                      />
                                    </div>
                                    
                                    {/* Scrollable content area */}
                                    <div className="flex-1 overflow-y-auto p-2">
                                      {loadingGifs ? (
                                        <div className="flex items-center justify-center h-full">
                                          <div className="text-center">
                                            <span className="mdi mdi-loading mdi-spin text-2xl"></span>
                                            <p className="text-sm text-neutral-500 mt-2">Loading GIFs...</p>
                                          </div>
                                        </div>
                                      ) : gifResults.length > 0 ? (
                                        <div className="grid grid-cols-3 gap-2">
                                          {gifResults.map((gif) => (
                                            <button 
                                              key={gif.id} 
                                              onClick={() => {
                                                const gifMarkdown = `![GIF](${gif.images.fixed_height.url})`;
                                                setCommentContent(prev => ({ 
                                                  ...prev, 
                                                  [headline.id]: `${prev[headline.id] || ''}${prev[headline.id] ? '\n' : ''}${gifMarkdown}` 
                                                }));
                                                setCommentGifOpenId(null);
                                              }}
                                              className="relative group overflow-hidden rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 aspect-square"
                                            >
                                              <img 
                                                src={gif.images.fixed_height_small.url} 
                                                alt={gif.title} 
                                                className="w-full h-full object-cover" 
                                                loading="lazy" 
                                              />
                                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
                                            </button>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center h-full text-center p-4">
                                          <div>
                                            <span className="mdi mdi-magnify text-3xl text-neutral-400"></span>
                                            <p className="text-sm text-neutral-500 mt-2">No GIFs found. Try a different search.</p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Footer with trending label */}
                                    {!gifSearchTerm && (
                                      <div className="px-3 py-2 border-t bg-neutral-50 dark:bg-neutral-800 flex-shrink-0">
                                        <p className="text-xs text-neutral-500">
                                          <span className="mdi mdi-trending-up mr-1"></span>
                                          Trending GIFs
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>

                              {/* Attach for comment */}
                              <div>
                                <input type="file" multiple className="hidden" id={`cfile-${headline.id}`}
                                  onChange={(e) => onPickCommentFiles(headline.id, e.target.files)} />
                                <Button variant="ghost" size="sm" onClick={() => (document.getElementById(`cfile-${headline.id}`) as HTMLInputElement)?.click()} className="h-8">
                                  <span className="mdi mdi-paperclip mr-1"></span> Attach
                                </Button>
                              </div>

                              {/* @ mention for comment with tabs */}
                              <Popover open={commentMentionOpen === headline.id} onOpenChange={(o) => setCommentMentionOpen(o ? headline.id : null)}>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8"><span className="mdi mdi-at mr-1"></span> Mention</Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-2" align="start">
                                  <Tabs defaultValue="people" className="w-full">
                                    <TabsList className="grid grid-cols-2 h-8 mb-2">
                                      <TabsTrigger value="people" className="text-xs">People</TabsTrigger>
                                      <TabsTrigger value="files" className="text-xs">Files</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="people">
                                      <Input placeholder="Search people..." value={commentMentionSearch} onChange={(e) => setCommentMentionSearch(e.target.value)} className="mb-2 h-8 text-xs" />
                                      <div className="max-h-56 overflow-auto space-y-1">
                                        {(usersForOrg as any[])
                                          .filter(u => (u.fullName || u.full_name || '').toLowerCase().includes(commentMentionSearch.toLowerCase()))
                                          .slice(0, 20)
                                          .map(u => (
                                            <Button key={u.id} variant="ghost" size="sm" className="w-full justify-start h-8 text-xs"
                                              onClick={() => addCommentMention(headline.id, (u.fullName || u.full_name || u.email || 'User'), u.id)}>
                                              <span className="mdi mdi-account-circle-outline mr-2"></span>
                                              {u.fullName || u.full_name || u.email}
                                            </Button>
                                          ))}
                                      </div>
                                    </TabsContent>
                                    <TabsContent value="files">
                                      <div className="text-xs text-neutral-500 p-2">Folders &amp; documents picker coming soon.</div>
                                    </TabsContent>
                                  </Tabs>
                                </PopoverContent>
                              </Popover>

                              <Button size="sm" className="h-8"
                                onClick={() => createComment.mutate({ 
                                  headlineId: headline.id, 
                                  content: commentContent[headline.id] || '',
                                  files: commentFiles[headline.id] || []
                                })}
                                disabled={!commentContent[headline.id]?.trim() || createComment.isPending}
                              >
                                {createComment.isPending ? (
                                  <>
                                    <span className="mdi mdi-loading mdi-spin mr-1"></span>
                                    Posting
                                  </>
                                ) : 'Post Comment'}
                              </Button>
                            </div>
                            {(commentFiles[headline.id]?.length || 0) > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {(commentFiles[headline.id] || []).map(f => (
                                  <Badge key={f.name} variant="secondary" className="flex items-center gap-1">
                                    <span className="mdi mdi-paperclip" /> {f.name}
                                    <button className="ml-1" onClick={() => removeCommentFile(headline.id, f.name)}>
                                      <span className="mdi mdi-close" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {/* Render local previews for submitted comments */}
                            {commentsData[headline.id]?.map(c => (
                              <div key={`prev-${c.id}`} className="mt-1">
                                {(commentLocalFiles[c.id]?.length || 0) > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {(commentLocalFiles[c.id] || []).map((f) => {
                                      const isImage = (f.type || '').startsWith('image/');
                                      const url = URL.createObjectURL(f);
                                      return (
                                        <div key={f.name} className="relative">
                                          {isImage ? (
                                            <img src={url} alt={f.name} className="h-28 w-28 object-cover rounded cursor-pointer"
                                              onClick={() => setPreviewUrl(url)} />
                                          ) : (
                                            <Button variant="secondary" size="sm" className="h-7 text-xs" onClick={() => window.open(url, '_blank')}>
                                              <span className="mdi mdi-paperclip mr-1" /> {f.name}
                                            </Button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
        {/* Comments List */}
        {(() => {
          const comments = commentsData[headline.id] || [];
          console.log('🎨 Rendering comments section:', { 
            headlineId: headline.id, 
            commentsCount: comments.length, 
            loading: loadingComments[headline.id],
            comments 
          });
          if (loadingComments[headline.id]) {
            return <div className="text-center py-2 text-sm text-neutral-500">Loading comments...</div>;
          }
          if (comments.length > 0) {
            return (
              <div className="space-y-2 mt-2">
                {renderComments(comments, null, 0)}
              </div>
            );
          }
          return <div className="text-center py-2 text-sm text-neutral-500">No comments yet</div>;
        })()}
                      </div>
                    )}
                  </Card>
                );
              })
            ) : (
              // Show message when no posts match filters
              <Card className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <span className="mdi mdi-message-text-outline text-6xl text-neutral-300 dark:text-neutral-600 mb-4 block"></span>
                  <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
                  <p className="text-neutral-500 dark:text-neutral-400 mb-6">
                    {selectedTab === 'location' ? 
                      'Be the first to share an update with your location!' :
                      selectedTab === 'role' ?
                      'Be the first to share an update with your role!' :
                      'No posts have been shared yet. Be the first!'
                    }
                  </p>
                  <Button onClick={() => setDialogOpen(true)}>
                    <span className="mdi mdi-pencil mr-2"></span>
                    Create First Post
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Edit Headline Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
            <DialogDescription>
              Make changes to your post.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={editHeadlineContent.title}
                onChange={(e) => setEditHeadlineContent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter title..."
              />
            </div>
            <div>
              <Label htmlFor="edit-content">Content</Label>
              <Textarea
                id="edit-content"
                value={editHeadlineContent.content}
                onChange={(e) => setEditHeadlineContent(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter content..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingHeadline) {
                  updateHeadline.mutate({
                    headlineId: editingHeadline.id,
                    title: editHeadlineContent.title,
                    content: editHeadlineContent.content
                  });
                  setEditDialogOpen(false);
                  setEditingHeadline(null);
                }
              }}
              disabled={!editHeadlineContent.title || !editHeadlineContent.content}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Share Post</DialogTitle>
            <DialogDescription>
              Share this post to another board you have access to.
            </DialogDescription>
          </DialogHeader>
          
          {sharingPost && (
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-4 p-1">
                {/* Original Post Preview */}
                <div className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-3">
                  <h4 className="font-semibold text-sm mb-1">{sharingPost.title}</h4>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-2">
                    {sharingPost.content}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    By {sharingPost.author}
                  </p>
                </div>
                
                {/* Additional Message */}
                <div>
                  <Label htmlFor="shareMessage" className="text-sm">Add a message (optional)</Label>
                  <Textarea
                    id="shareMessage"
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder="Add your thoughts about this post..."
                    className="text-sm mt-1"
                    rows={3}
                  />
                </div>
                
                {/* Board Selection */}
                <div>
                  <Label className="text-sm">Select Board to Share To</Label>
                  <Tabs value={shareBoardType} onValueChange={(v) => setShareBoardType(v as 'location' | 'role')} className="mt-2">
                    <TabsList className="grid w-full grid-cols-2 h-8">
                      <TabsTrigger value="location" className="text-xs">Location Boards</TabsTrigger>
                      <TabsTrigger value="role" className="text-xs">Role Boards</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="location" className="mt-3">
                      <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                        {userAccessibleBoards.location
                          .filter(board => {
                            // Don't allow sharing to the same board
                            if (sharingPost.locationId === board.id) return false;
                            return true;
                          })
                          .map(board => (
                            <Button
                              key={board.id}
                              variant={selectedShareBoard === board.id && shareBoardType === 'location' ? 'default' : 'outline'}
                              className="justify-start text-xs h-8"
                              onClick={() => setSelectedShareBoard(board.id)}
                            >
                              {board.name}
                            </Button>
                          ))}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="role" className="mt-3">
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {userAccessibleBoards.role
                          .filter(board => {
                            // Don't allow sharing to the same board
                            if (sharingPost.targetRole) {
                              const normalizedTargetRole = normalizeRole(sharingPost.targetRole);
                              const boardId = ROLE_TO_BOARD_ID[normalizedTargetRole || sharingPost.targetRole] || sharingPost.targetRole;
                              if (boardId === board.id || normalizedTargetRole === board.id) return false;
                            }
                            return true;
                          })
                          .map(board => (
                            <Button
                              key={board.id}
                              variant={selectedShareBoard === board.id && shareBoardType === 'role' ? 'default' : 'outline'}
                              className="justify-start text-xs h-8 w-full"
                              onClick={() => setSelectedShareBoard(board.id)}
                            >
                              {board.name}
                            </Button>
                          ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShareDialogOpen(false);
                setShareMessage('');
                setSelectedShareBoard('');
              }}
              className="h-8 text-sm"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (sharingPost && selectedShareBoard) {
                  const boardName = shareBoardType === 'location' 
                    ? userAccessibleBoards.location.find(b => b.id === selectedShareBoard)?.name
                    : userAccessibleBoards.role.find(b => b.id === selectedShareBoard)?.name;
                    
                  shareHeadline.mutate({
                    headlineId: sharingPost.id,
                    toBoardId: selectedShareBoard,
                    toBoardType: shareBoardType,
                    toBoardName: boardName || selectedShareBoard,
                    additionalMessage: shareMessage
                  });
                }
              }}
              disabled={!selectedShareBoard || shareHeadline.isPending}
              className="h-8 text-sm"
            >
              {shareHeadline.isPending ? "Sharing..." : "Share Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Simple image preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null);} }}>
        <DialogContent className="max-w-[90vw]">
          {previewUrl && (
            <img src={previewUrl} alt="Preview" className="max-h-[80vh] w-auto mx-auto" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
